import Feature from 'ol/Feature';
import LineString from 'ol/geom/LineString';
import Point from 'ol/geom/Point';
import Polygon from 'ol/geom/Polygon';
import { get as getProjection, transform } from 'ol/proj';
import { boundingExtent, getIntersection, isEmpty } from 'ol/extent';
import { isCombinedFormatted } from '../types.js';
import { pointInRing, pointInRings } from '../clipping/pointInRing.js';
import { PolygonEdgeIndex } from '../clipping/PolygonEdgeIndex.js';
import { clipPolylineToPolygon, createClipScratch, } from '../clipping/clipPolylineToPolygon.js';
import { clipPolygonToConvex } from '../clipping/clipPolygonToConvex.js';
import { densifyRing, projectRing } from '../clipping/densifyRing.js';
import { snapRingToCellGrid } from '../clipping/snapRingToCellGrid.js';
import { transformExtentSampled } from '../util/geo.js';
import { ParseError } from '../util/ParseError.js';
import { requireTransform } from '../util/requireTransform.js';
// Screen-space slack the snapped clip ring is inflated by, so a grid line lying
// ON a ring edge survives its own sub-pixel densification error.
const CLIP_SLACK_PX = 2;
// Ceiling on that slack, as a fraction of the snap interval, so zooming far out
// cannot inflate the coverage by a visible part of a cell.
const MAX_CLIP_SLACK_RATIO = 0.05;
/** Wraps a GridSystem to clip its features and labels against a polygon. */
export class PolygonClippedGridSystem {
    source_;
    polygonCrs_;
    sourceRingOpen_;
    densifiedSourceRing_;
    ringStepsPerEdge_;
    emitBoundary_;
    cellSnapInterval_;
    viewCache_ = new Map();
    clipScratch_ = createClipScratch();
    lastSnapRingsInPolygonCrs_ = null;
    constructor(options) {
        this.source_ = options.source;
        this.polygonCrs_ = options.clipPolygon.crs;
        const rings = options.clipPolygon.rings;
        if (rings.length === 0 || rings[0].length < 3) {
            throw new Error('PolygonClippedGridSystem: clipPolygon.rings must contain at least ' +
                'one outer ring with 3+ vertices');
        }
        this.sourceRingOpen_ = rings[0];
        this.ringStepsPerEdge_ = options.ringStepsPerEdge ?? 4;
        this.densifiedSourceRing_ = densifyRing(this.sourceRingOpen_, this.ringStepsPerEdge_);
        this.emitBoundary_ = options.emitBoundary ?? true;
        this.cellSnapInterval_ = options.cellSnapInterval;
    }
    getFeatures(extent, resolution, viewProjection) {
        const view = this.viewState_(viewProjection, resolution);
        const clippedExtent = getIntersection(extent, view.index.ringExtent);
        if (isEmpty(clippedExtent))
            return [];
        const sourceFeatures = this.source_.getFeatures(clippedExtent, resolution, viewProjection);
        const out = [];
        for (const feature of sourceFeatures) {
            const geom = feature.getGeometry();
            if (!(geom instanceof LineString)) {
                out.push(feature);
                continue;
            }
            const flat = geom.getFlatCoordinates();
            const stride = geom.getStride();
            const clipped = clipPolylineToPolygon(flat, 0, flat.length, stride, view.index, this.clipScratch_);
            for (const piece of clipped) {
                const f = new Feature({
                    geometry: new LineString(piece, 'XY'),
                });
                copyFeatureProperties_(feature, f);
                out.push(f);
            }
        }
        if (this.emitBoundary_ &&
            !view.isSnap &&
            extentOverlaps_(extent, view.index.ringExtent)) {
            for (let r = 0; r < view.projectedRings.length; r++) {
                out.push(this.buildBoundaryFeature_(view.projectedRings[r]));
            }
        }
        return out;
    }
    getLabels(extent, resolution, viewProjection) {
        const view = this.viewState_(viewProjection, resolution);
        const clippedExtent = getIntersection(extent, view.index.ringExtent);
        if (isEmpty(clippedExtent))
            return [];
        const labels = this.source_.getLabels(clippedExtent, resolution, viewProjection);
        const ringsPolyCrs = view.polyCrsRings;
        const polyExtent = transformExtentSampled(clippedExtent, view.viewToPolygon);
        if (!isFinite(polyExtent[0]))
            return [];
        const [pMinX, pMinY, pMaxX, pMaxY] = polyExtent;
        return labels.filter((label) => {
            const [vx, vy] = label.point.getCoordinates();
            const polyXY = view.viewToPolygon([vx, vy], undefined, 2);
            const px = polyXY[0];
            const py = polyXY[1];
            if (px === undefined || py === undefined)
                return false;
            if (!isFinite(px) || !isFinite(py))
                return false;
            const axis = label.axis;
            const eps = axis === 'x'
                ? Math.max(Math.abs(px), pMaxX - pMinX, 1) * 1e-9
                : Math.max(Math.abs(py), pMaxY - pMinY, 1) * 1e-9;
            const midX = axis === 'x' ? px : (pMinX + pMaxX) * 0.5;
            const midY = axis === 'x' ? (pMinY + pMaxY) * 0.5 : py;
            if (view.polyCrsIndex.pointInRing(midX, midY))
                return true;
            for (let r = 0; r < ringsPolyCrs.length; r++) {
                const hit = axis === 'x'
                    ? gridLineCrossesRing_(px, 'x', pMinY, pMaxY, ringsPolyCrs[r], eps)
                    : gridLineCrossesRing_(py, 'y', pMinX, pMaxX, ringsPolyCrs[r], eps);
                if (hit)
                    return true;
            }
            return false;
        });
    }
    getCellLabels(extent, resolution, viewProjection) {
        if (!this.source_.getCellLabels)
            return [];
        const labels = this.source_.getCellLabels(extent, resolution, viewProjection);
        const view = this.viewState_(viewProjection, resolution);
        const rings = view.projectedRings;
        const clipRing = rings[0];
        const out = [];
        for (const label of labels) {
            const [cx, cy] = label.point.getCoordinates();
            if (cx === undefined || cy === undefined)
                continue;
            const cellRing = label.cellRing;
            if (!cellRing || cellRing.length < 3 || !clipRing) {
                if (view.index.pointInRing(cx, cy))
                    out.push(label);
                continue;
            }
            const allInside = ringFullyInsideIndexed_(cellRing, view.index);
            if (allInside) {
                out.push(label);
                continue;
            }
            const clipped = clipPolygonToConvex(cellRing, clipRing);
            if (clipped.length < 3)
                continue;
            const interior = new Polygon([clipped]).getFlatInteriorPoint();
            const [ix, iy] = interior;
            if (ix === undefined ||
                iy === undefined ||
                !Number.isFinite(ix) ||
                !Number.isFinite(iy))
                continue;
            const replaced = {
                point: new Point([ix, iy]),
                text: label.text,
                cellSizePx: clippedCellSizePx_(cellRing, clipped, label.cellSizePx),
            };
            if (label.cellRing)
                replaced.cellRing = label.cellRing;
            out.push(replaced);
        }
        return out;
    }
    isValidCoordinate(coordinate, viewProjection) {
        if (this.source_.isValidCoordinate &&
            !this.source_.isValidCoordinate(coordinate, viewProjection)) {
            return false;
        }
        return this.coordIsInsidePolygon_(coordinate, viewProjection);
    }
    formatCoordinate(coordinate, viewProjection) {
        if (this.isValidCoordinate(coordinate, viewProjection)) {
            return this.source_.formatCoordinate(coordinate, viewProjection);
        }
        const sample = this.source_.formatCoordinate(coordinate, viewProjection);
        return isCombinedFormatted(sample) ? { combined: '-' } : { x: '-', y: '-' };
    }
    parseCoordinate(text, viewProjection) {
        if (!this.source_.parseCoordinate) {
            throw new ParseError(text, 'source grid system does not support parseCoordinate');
        }
        return this.source_.parseCoordinate(text, viewProjection);
    }
    viewState_(viewProjection, resolution) {
        const snapInterval = this.cellSnapInterval_?.(resolution, viewProjection);
        const code = projectionCacheKey_(viewProjection);
        const slack = snapInterval === undefined
            ? 0
            : Math.min(snapInterval * MAX_CLIP_SLACK_RATIO, Math.max(snapInterval * 1e-3, this.pixelSlack_(viewProjection, resolution)));
        const variant = snapInterval !== undefined ? `snap:${snapInterval}:${slack}` : 'raw';
        const key = `${code}|${variant}`;
        const cached = this.viewCache_.get(key);
        if (cached)
            return cached;
        const clipRingsInPolygonCrs = this.buildClipRings_(snapInterval, slack);
        const projectedRings = projectRingList_(clipRingsInPolygonCrs, this.polygonCrs_, viewProjection);
        const state = {
            projectedRings,
            index: new PolygonEdgeIndex(projectedRings),
            polyCrsRings: clipRingsInPolygonCrs,
            polyCrsIndex: new PolygonEdgeIndex(clipRingsInPolygonCrs),
            viewToPolygon: requireTransform(viewProjection, this.polygonCrs_),
            isSnap: snapInterval !== undefined,
        };
        this.viewCache_.set(key, state);
        return state;
    }
    buildClipRings_(snapInterval, slack) {
        if (snapInterval === undefined) {
            this.lastSnapRingsInPolygonCrs_ = null;
            return [this.densifiedSourceRing_];
        }
        const snapped = snapRingToCellGrid(this.sourceRingOpen_, snapInterval);
        if (snapped.length === 0) {
            this.lastSnapRingsInPolygonCrs_ = null;
            return [this.densifiedSourceRing_];
        }
        this.lastSnapRingsInPolygonCrs_ = snapped.map((r) => r.slice());
        return snapped.map((ring) => densifyRing(inflateRectilinearRing_(ring, slack), this.ringStepsPerEdge_));
    }
    /**
     * Clip slack in polygon-CRS units, worth {@link CLIP_SLACK_PX} screen pixels
     * and rounded up to a power of two so it changes only once per zoom band.
     * A source line coincident with a snapped ring edge is only as accurate as its
     * own densification (about a pixel of chord sag), so a clip tighter than that
     * chops it into fragments.
     */
    pixelSlack_(viewProjection, resolution) {
        if (!(resolution > 0) || !isFinite(resolution))
            return 0;
        const toView = requireTransform(this.polygonCrs_, viewProjection);
        const toPolygon = requireTransform(viewProjection, this.polygonCrs_);
        const origin = this.sourceRingOpen_[0];
        if (!origin)
            return 0;
        const [vx, vy] = toView([origin[0], origin[1]], undefined, 2);
        if (vx === undefined || vy === undefined)
            return 0;
        const step = CLIP_SLACK_PX * resolution;
        const [px, py] = toPolygon([vx + step, vy], undefined, 2);
        if (px === undefined || py === undefined)
            return 0;
        const spanned = Math.hypot(px - origin[0], py - origin[1]);
        if (!(spanned > 0) || !isFinite(spanned))
            return 0;
        return 2 ** Math.ceil(Math.log2(spanned));
    }
    coordIsInsidePolygon_(coordinate, viewProjection) {
        const projCode = projectionCacheKey_(viewProjection);
        const polyCode = projectionCacheKey_(this.polygonCrs_);
        const [x, y] = projCode === polyCode
            ? coordinate
            : transform(coordinate, viewProjection, this.polygonCrs_);
        if (x === undefined || y === undefined)
            return false;
        if (!isFinite(x) || !isFinite(y))
            return false;
        if (this.lastSnapRingsInPolygonCrs_) {
            return pointInRings(x, y, this.lastSnapRingsInPolygonCrs_);
        }
        return pointInRing(x, y, this.sourceRingOpen_);
    }
    buildBoundaryFeature_(ring) {
        return new Feature({
            geometry: new LineString([...ring, ring[0]]),
            gridLineType: 'boundary',
        });
    }
}
function projectionCacheKey_(projection) {
    const resolved = getProjection(projection);
    if (resolved)
        return resolved.getCode();
    return String(projection);
}
function extentOverlaps_(a, b) {
    return !(a[2] < b[0] || a[0] > b[2] || a[3] < b[1] || a[1] > b[3]);
}
function ringFullyInsideIndexed_(ring, index) {
    for (let i = 0; i < ring.length; i++) {
        const p = ring[i];
        if (!index.pointInRing(p[0], p[1]))
            return false;
    }
    return true;
}
function clippedCellSizePx_(original, clipped, originalSizePx) {
    const orig = ringMinExtent_(original);
    const clip = ringMinExtent_(clipped);
    if (orig <= 0)
        return originalSizePx;
    return originalSizePx * (clip / orig);
}
function ringMinExtent_(ring) {
    const [minX, minY, maxX, maxY] = boundingExtent(ring);
    const w = maxX - minX;
    const h = maxY - minY;
    return w < h ? w : h;
}
function projectRingList_(rings, from, to) {
    const out = [];
    for (let r = 0; r < rings.length; r++) {
        const pr = projectRing(rings[r], from, to);
        if (pr.length >= 3)
            out.push(pr);
    }
    return out;
}
function inflateRectilinearRing_(ring, eps) {
    const n = ring.length;
    const out = new Array(n);
    for (let i = 0; i < n; i++) {
        const prev = ring[(i + n - 1) % n];
        const cur = ring[i];
        const next = ring[(i + 1) % n];
        const [n1x, n1y] = unitOutwardNormal_(cur[0] - prev[0], cur[1] - prev[1]);
        const [n2x, n2y] = unitOutwardNormal_(next[0] - cur[0], next[1] - cur[1]);
        const denom = 1 + n1x * n2x + n1y * n2y;
        const scale = denom > 1e-12 ? eps / denom : eps * 0.5;
        out[i] = [cur[0] + (n1x + n2x) * scale, cur[1] + (n1y + n2y) * scale];
    }
    return out;
}
function unitOutwardNormal_(dx, dy) {
    const len = Math.hypot(dx, dy);
    if (len === 0)
        return [0, 0];
    return [dy / len, -dx / len];
}
function gridLineCrossesRing_(value, axis, rangeMin, rangeMax, ring, eps = 0) {
    const n = ring.length;
    for (let i = 0; i < n; i++) {
        const a = ring[i];
        const b = ring[(i + 1) % n];
        const a0 = axis === 'x' ? a[0] : a[1];
        const a1 = axis === 'x' ? a[1] : a[0];
        const b0 = axis === 'x' ? b[0] : b[1];
        const b1 = axis === 'x' ? b[1] : b[0];
        const da = a0 - value;
        const db = b0 - value;
        if (da > eps && db > eps)
            continue;
        if (da < -eps && db < -eps)
            continue;
        if (da === db)
            continue;
        const t = da / (da - db);
        const cross = a1 + t * (b1 - a1);
        if (cross >= rangeMin && cross <= rangeMax)
            return true;
    }
    return false;
}
function copyFeatureProperties_(src, dst) {
    for (const key of ['gridLineType', 'gridAxis', 'gridValue']) {
        const value = src.get(key);
        if (value !== undefined)
            dst.set(key, value);
    }
}
//# sourceMappingURL=PolygonClippedGridSystem.js.map